// components/scheduler/ReminderConfigList.tsx
import React from 'react';
import { Table, Tag, Button, Space, Switch, Tooltip, Popconfirm, Badge } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    PlayCircleOutlined,
    ClockCircleOutlined,
    ApiOutlined,
    MessageOutlined,
    WhatsAppOutlined,
    MailOutlined
} from '@ant-design/icons';
import { ReminderConfigDTO, NotificationPlatforms } from '@/shared/api/model';

interface ReminderConfigListProps {
    configs: ReminderConfigDTO[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onToggleStatus: (id: string, enabled: boolean) => void;
    onTrigger: (id: string) => void;
    isLoading: boolean;
    triggeringId: string | null;
    scheduledConfigs: Record<string, boolean>;
}

const ReminderConfigList: React.FC<ReminderConfigListProps> = ({
                                                                   configs,
                                                                   onEdit,
                                                                   onDelete,
                                                                   onToggleStatus,
                                                                   onTrigger,
                                                                   isLoading,
                                                                   triggeringId,
                                                                   scheduledConfigs
                                                               }) => {
    // Get platform icon
    const getPlatformIcon = (platform: NotificationPlatforms) => {
        switch (platform) {
            case NotificationPlatforms.SMS:
                return <MessageOutlined />;
            case NotificationPlatforms.WHATS_APP:
                return <WhatsAppOutlined />;
            case NotificationPlatforms.EMAIL:
                return <MailOutlined />;
            default:
                return null;
        }
    };

    // Format reminder time
    const formatReminderTime = (hour?: number, minute?: number) => {
        if (hour === undefined || minute === undefined) return '';

        const hourValue = hour % 12 || 12;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        return `${hourValue}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };

    // Table columns
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: ReminderConfigDTO) => (
                <div>
                    <div className="font-medium">{text}</div>
                    {record.description && (
                        <div className="text-xs text-gray-500">{record.description}</div>
                    )}
                </div>
            )
        },
        {
            title: 'Notification Type',
            dataIndex: 'queryServiceName',
            key: 'queryServiceName',
            render: (queryServiceName: string) => (
                <Tag icon={<ApiOutlined />} color="blue">
                    {queryServiceName}
                </Tag>
            )
        },
        {
            title: 'Template',
            dataIndex: 'templateName',
            key: 'templateName',
            render: (templateName: string, record: ReminderConfigDTO) => (
                <Space>
                    { // @ts-ignore
                        getPlatformIcon(record.platform)}
                    <span>{templateName}</span>
                </Space>
            )
        },
        {
            title: 'Schedule',
            key: 'schedule',
            render: (text: string, record: ReminderConfigDTO) => (
                <Tooltip title={`Scheduled to run at ${formatReminderTime(record.reminderHour, record.reminderMinute)} daily`}>
                    <Tag icon={<ClockCircleOutlined />} color="green">
                        {formatReminderTime(record.reminderHour, record.reminderMinute)}
                    </Tag>
                </Tooltip>
            )
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (enabled: boolean, record: ReminderConfigDTO) => (
                <Switch
                    checked={enabled}
                    onChange={
                        // @ts-ignore
                    (checked) => onToggleStatus(record.id, checked)
                }
                />
            )
        },
        {
            title: 'Scheduled',
            key: 'scheduled',
            render: (text: string, record: ReminderConfigDTO) => (
                <Badge
                    // @ts-ignore
                    status={scheduledConfigs[record.id] ? "success" : "default"}
                    // @ts-ignore
                    text={scheduledConfigs[record.id] ? "Active" : "Inactive"}
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: string, record: ReminderConfigDTO) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        // @ts-ignore
                        onClick={() => onEdit(record.id)}
                    />
                    <Popconfirm
                        title="Delete reminder configuration"
                        description="Are you sure you want to delete this configuration?"
                        // @ts-ignore
                        onConfirm={() => onDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                    <Button
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        // @ts-ignore
                        onClick={() => onTrigger(record.id)}
                        loading={triggeringId === record.id}
                        disabled={!record.enabled}
                    >
                        Trigger
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <Table
            dataSource={configs}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
        />
    );
};

export default ReminderConfigList;